import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { CloseCircleOutlined } from '@ant-design/icons'
import { useQuery } from '@apollo/client'
import { useTranslation } from '@zauberware/react-i18n/web'
import { Space, Menu, Tag, Button } from 'antd'
import {
  DROPDOWN_ASSET_CONTENTS_QUERY,
  DROPDOWN_SEGMENTATIONS_QUERY,
  DROPDOWN_GTM_SEGMENTATIONS_QUERY,
  DROPDOWN_BRANDLINES_QUERY,
  DROPDOWN_CAMPAIGNS_QUERY,
  DROPDOWN_USERS_QUERY
} from '../../../services/graphql/queries'
import { Dropdown } from '../../index'

const mapStateToProps = ({ user }) => ({
  isAreaManager: user.isAreaManager
})

/**
 * Shared Filters component for report views
 * Supports configurable filter types through filterConfig prop
 *
 * @param {Object} filters - Current filter values
 * @param {Function} setFilters - Function to update filters
 * @param {string} storageKey - localStorage key for filter persistence
 * @param {Object} filterConfig - Configuration object defining which filters to show
 * @param {string} areaManagerStorageKey - Optional separate localStorage key for area manager
 * @param {number} areaManagerId - Optional separate area manager ID state
 * @param {Function} setAreaManagerId - Optional setter for area manager ID
 * @param {boolean} isAreaManager - Whether current user is an area manager
 */
const ReportFilters = ({
  filters,
  setFilters,
  storageKey,
  filterConfig,
  areaManagerStorageKey,
  areaManagerId: externalAreaManagerId,
  setAreaManagerId: externalSetAreaManagerId,
  textSearch,
  onClearSearch,
  isAreaManager = false
}) => {
  const { t } = useTranslation('filters')

  // Determine if using separate area manager storage
  const useSeparateAreaManager = !!areaManagerStorageKey
  const areaManagerId = useSeparateAreaManager ? externalAreaManagerId : filters?.areaManagerId
  const setAreaManagerId = useSeparateAreaManager ? externalSetAreaManagerId : null

  const setFiltersAndStore = newFilters => {
    localStorage.setItem(storageKey, JSON.stringify(newFilters))
    setFilters(newFilters)
  }

  const { data: dropdownBrandlines } = useQuery(DROPDOWN_BRANDLINES_QUERY, {
    skip: !filterConfig.brandLines,
    fetchPolicy: 'cache-and-network',
    variables: {
      sortBy: { direction: 'asc', attribute: 'name' }
    }
  })

  // Campaigns Query
  const { data: dropdownCampaigns } = useQuery(DROPDOWN_CAMPAIGNS_QUERY, {
    skip: !filterConfig.campaigns,
    fetchPolicy: 'cache-and-network'
  })

  // Segmentations Query (for Customer Reports)

  const { data: dropdownSegmentationsCustomerGroup } = useQuery(DROPDOWN_SEGMENTATIONS_QUERY, {
    skip: !filterConfig.segmentation,
    fetchPolicy: 'cache-and-network',
    variables: {
      sortBy: { attribute: 'name', direction: 'asc' }
    }
  })

  const { data: dropdownGtmSegmentationsCustomerGroups } = useQuery(
    DROPDOWN_GTM_SEGMENTATIONS_QUERY,
    {
      skip: !filterConfig.segmentation,
      fetchPolicy: 'cache-and-network',
      variables: {
        sortBy: { attribute: 'name', direction: 'asc' }
      }
    }
  )

  // Users Query (for sales reps and area managers)
  const { data: dropdownRepsUsers } = useQuery(DROPDOWN_USERS_QUERY, {
    skip: !filterConfig.salesReps && !filterConfig.areaManager,
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        archived: false,
        campaignNames: filters?.campaignNames,
        brandLineIds: filters?.brandLineIds,
        isAreaManager: filterConfig.salesReps ? undefined : true // Only area managers if not showing sales reps
      },
      sortBy: { direction: 'asc', attribute: 'lastName' },
      limit: 0
    }
  })

  // Assets Query
  const { data: dropDownAssets } = useQuery(DROPDOWN_ASSET_CONTENTS_QUERY, {
    skip: !filterConfig.assets,
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        archived: false,
        textSearch: '',
        campaignNames: filters?.campaignNames,
        brandLineIds: filters?.brandLineIds
      },
      sortBy: { attribute: 'title', direction: 'asc' },
      limit: 500
    }
  })

  // Filter Handlers
  const handleBrandLines = values => {
    setFiltersAndStore({
      ...filters,
      brandLineIds: values?.length > 0 ? values.map(v => parseInt(v)) : undefined
    })
  }

  const handleCampaigns = campaignNames => {
    setFiltersAndStore({
      ...filters,
      campaignNames: campaignNames?.length > 0 ? campaignNames : undefined
    })
  }

  const handleSegmentation = values => {
    setFiltersAndStore({
      ...filters,
      segmentationIds: values?.length > 0 ? values : undefined
    })
  }

  const handleReps = values => {
    setFiltersAndStore({
      ...filters,
      userIds: values?.length > 0 ? values.map(v => parseInt(v)) : undefined
    })
  }

  const handleAreaManager = values => {
    const value = values?.length > 0 ? parseInt(values[0]) : undefined
    if (useSeparateAreaManager) {
      localStorage.setItem(areaManagerStorageKey, JSON.stringify({ areaManagerId: value }))
      setAreaManagerId(value)
    } else {
      setFiltersAndStore({ ...filters, areaManagerId: value })
    }
  }

  const handleAssets = values => {
    setFiltersAndStore({
      ...filters,
      assetIds: values?.length > 0 ? values.map(v => parseInt(v)) : undefined
    })
  }

  const handleClearAllFilters = () => {
    setFiltersAndStore({})
    if (useSeparateAreaManager) {
      localStorage.setItem(areaManagerStorageKey, JSON.stringify({ areaManagerId: undefined }))
      setAreaManagerId(undefined)
    }
    if (onClearSearch) {
      onClearSearch()
    }
  }

  const removeFilter = (filterType, value) => {
    switch (filterType) {
      case 'brandLineIds':
        setFiltersAndStore({
          ...filters,
          brandLineIds: filters.brandLineIds.filter(id => id !== value)
        })
        break
      case 'campaignNames':
        setFiltersAndStore({
          ...filters,
          campaignNames: filters.campaignNames.filter(name => name !== value)
        })
        break
      case 'segmentationIds':
        setFiltersAndStore({
          ...filters,
          segmentationIds: filters.segmentationIds.filter(name => name !== value)
        })
        break
      case 'userIds':
        setFiltersAndStore({
          ...filters,
          userIds: filters.userIds.filter(id => id !== value)
        })
        break
      case 'areaManagerId':
        if (useSeparateAreaManager) {
          localStorage.setItem(areaManagerStorageKey, JSON.stringify({ areaManagerId: undefined }))
          setAreaManagerId(undefined)
        } else {
          setFiltersAndStore({ ...filters, areaManagerId: undefined })
        }
        break
      case 'assetIds':
        setFiltersAndStore({
          ...filters,
          assetIds: filters.assetIds.filter(id => id !== value)
        })
        break
      default:
        break
    }
  }

  // Check if any filters are active
  const hasActiveFilters =
    filters?.brandLineIds?.length > 0 ||
    filters?.campaignNames?.length > 0 ||
    filters?.segmentationIds?.length > 0 ||
    filters?.userIds?.length > 0 ||
    filters?.assetIds?.length > 0 ||
    areaManagerId

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* Filter Dropdowns */}
      <Space wrap>
        {/* Brand Lines */}
        {filterConfig.brandLines && (
          <Dropdown
            mode="multiple"
            value={filters?.brandLineIds?.map(String) || []}
            onChange={handleBrandLines}
            title={t('BrandLines')}
            scrollable
          >
            {dropdownBrandlines?.brandLines?.collection?.map(bl => (
              <Menu.Item key={String(bl.id)} value={bl.id}>
                {bl.name}
              </Menu.Item>
            ))}
          </Dropdown>
        )}

        {/* Campaigns */}
        {filterConfig.campaigns && (
          <Dropdown
            mode="multiple"
            value={filters?.campaignNames || []}
            onChange={handleCampaigns}
            title={t('Campaigns')}
            scrollable
          >
            {dropdownCampaigns?.campaignNames?.map(c => (
              <Menu.Item key={c} value={c}>
                {c}
              </Menu.Item>
            ))}
          </Dropdown>
        )}

        {/* Segmentation (Customer Reports only) */}
        {filterConfig.segmentation && (
          <Dropdown
            mode="multiple"
            value={filters?.segmentationIds || []}
            onChange={handleSegmentation}
            title={t('segmentation')}
            scrollable
          >
            {dropdownSegmentationsCustomerGroup?.customerGroups?.collection?.map(s => (
              <Menu.Item key={s.name} value={s.name}>
                {s.name}
              </Menu.Item>
            ))}
            {dropdownGtmSegmentationsCustomerGroups?.customerGtmGroups?.collection?.map(g => (
              <Menu.Item key={g.name} value={g.name}>
                {g.name}
              </Menu.Item>
            ))}
          </Dropdown>
        )}

        {/* Sales Reps */}
        {filterConfig.salesReps && (
          <Dropdown
            mode="multiple"
            value={filters?.userIds?.map(String) || []}
            onChange={handleReps}
            title={t('Reps')}
            scrollable
          >
            {dropdownRepsUsers?.users?.collection?.map(r => (
              <Menu.Item key={String(r.id)} value={r.id}>
                {r.name}
              </Menu.Item>
            ))}
          </Dropdown>
        )}

        {/* Area Manager */}
        {filterConfig.areaManager && !isAreaManager && (
          <Dropdown
            mode="multiple"
            value={areaManagerId ? [String(areaManagerId)] : []}
            onChange={handleAreaManager}
            title={t('areaManger')}
            scrollable
          >
            {dropdownRepsUsers?.users?.collection
              ?.filter(r => r.isAreaManager)
              .map(r => (
                <Menu.Item key={String(r.id)} value={r.id}>
                  {r.name}
                </Menu.Item>
              ))}
          </Dropdown>
        )}

        {/* Assets */}
        {filterConfig.assets && (
          <Dropdown
            mode="multiple"
            value={filters?.assetIds?.map(String) || []}
            onChange={handleAssets}
            title={t('Assets')}
            scrollable
          >
            {dropDownAssets?.assetContents?.collection?.map(a => (
              <Menu.Item key={String(a.id)} value={a.id}>
                {a.title}
              </Menu.Item>
            ))}
          </Dropdown>
        )}

        {/* Clear All Button */}
        {hasActiveFilters && (
          <Button type="link" danger icon={<CloseCircleOutlined />} onClick={handleClearAllFilters}>
            {t('clearAll')}
          </Button>
        )}
      </Space>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Space wrap size="small">
          {/* Brand Lines Tags */}
          {filters?.brandLineIds?.map(id => {
            const brandLine = dropdownBrandlines?.brandLines?.collection?.find(bl => bl.id === id)
            return (
              <Tag
                key={`brandline-${id}`}
                closable
                onClose={() => removeFilter('brandLineIds', id)}
                color="blue"
              >
                {brandLine?.name || `ID: ${id}`}
              </Tag>
            )
          })}

          {/* Campaign Tags */}
          {filters?.campaignNames?.map(name => (
            <Tag
              key={`campaign-${name}`}
              closable
              onClose={() => removeFilter('campaignNames', name)}
              color="green"
            >
              {name}
            </Tag>
          ))}

          {/* Segmentation Tags */}
          {filters?.segmentationIds?.map(name => (
            <Tag
              key={`segmentation-${name}`}
              closable
              onClose={() => removeFilter('segmentationIds', name)}
              color="green"
            >
              {name}
            </Tag>
          ))}

          {/* Sales Reps Tags */}
          {filters?.userIds?.map(id => {
            const rep = dropdownRepsUsers?.users?.collection?.find(r => r.id === id)
            return (
              <Tag
                key={`rep-${id}`}
                closable
                onClose={() => removeFilter('userIds', id)}
                color="purple"
              >
                {rep?.name || `ID: ${id}`}
              </Tag>
            )
          })}

          {/* Area Manager Tag */}
          {areaManagerId && (
            <Tag
              key={`areamanager-${areaManagerId}`}
              closable
              onClose={() => removeFilter('areaManagerId', areaManagerId)}
              color="orange"
            >
              {dropdownRepsUsers?.users?.collection?.find(r => r.id === areaManagerId)?.name ||
                `ID: ${areaManagerId}`}
            </Tag>
          )}

          {/* Asset Tags */}
          {filters?.assetIds?.map(id => {
            const asset = dropDownAssets?.assetContents?.collection?.find(a => a.id === id)
            return (
              <Tag
                key={`asset-${id}`}
                closable
                onClose={() => removeFilter('assetIds', id)}
                color="cyan"
              >
                {asset?.title || `ID: ${id}`}
              </Tag>
            )
          })}
        </Space>
      )}
    </Space>
  )
}

ReportFilters.propTypes = {
  filters: PropTypes.object,
  setFilters: PropTypes.func.isRequired,
  storageKey: PropTypes.string.isRequired,
  filterConfig: PropTypes.shape({
    brandLines: PropTypes.bool,
    campaigns: PropTypes.bool,
    segmentation: PropTypes.bool,
    salesReps: PropTypes.bool,
    areaManager: PropTypes.bool,
    assets: PropTypes.bool
  }).isRequired,
  areaManagerStorageKey: PropTypes.string,
  areaManagerId: PropTypes.number,
  setAreaManagerId: PropTypes.func,
  textSearch: PropTypes.string,
  onClearSearch: PropTypes.func,
  isAreaManager: PropTypes.bool
}

export default connect(mapStateToProps)(ReportFilters)
